import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../../lib/api.js';
import { toast } from '../../components/ui/Toast.js';
import { Button } from '../../components/ui/Button.js';
import { Input } from '../../components/ui/Input.js';
import { Spinner } from '../../components/ui/Spinner.js';

interface AllowedDomain {
  id: string;
  domain: string;
  createdAt: string;
}

export default function AdminDomainsPage() {
  const [domains, setDomains] = useState<AllowedDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [newDomain, setNewDomain] = useState('');
  const [adding, setAdding] = useState(false);

  function load() {
    setLoading(true);
    api
      .get<{ domains: AllowedDomain[] }>('/api/admin/domains')
      .then((r) => setDomains(r.domains))
      .catch(() => toast.error('Failed to load domains'))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  async function handleAdd(e: FormEvent) {
    e.preventDefault();
    const domain = newDomain.trim().toLowerCase();
    if (!domain) return;
    setAdding(true);
    try {
      await api.post('/api/admin/domains', { domain });
      toast.success(`Added ${domain}`);
      setNewDomain('');
      load();
    } catch {
      toast.error('Failed to add domain');
    } finally {
      setAdding(false);
    }
  }

  async function handleRemove(id: string, domain: string) {
    if (!confirm(`Remove ${domain}?`)) return;
    try {
      await api.delete(`/api/admin/domains/${id}`);
      toast.success(`Removed ${domain}`);
      load();
    } catch {
      toast.error('Failed to remove domain');
    }
  }

  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-bold text-neutral-100 mb-2">Allowed Domains</h1>
      <p className="text-sm text-neutral-400 mb-6">
        Only users registering with an email from these domains can create accounts without an invite link.
      </p>

      {/* Add domain */}
      <form onSubmit={handleAdd} className="flex gap-2 mb-6">
        <Input
          placeholder="example.com"
          value={newDomain}
          onChange={(e) => setNewDomain(e.target.value)}
          className="flex-1"
        />
        <Button type="submit" isLoading={adding}>
          Add domain
        </Button>
      </form>

      {/* List */}
      {loading ? (
        <Spinner />
      ) : domains.length === 0 ? (
        <p className="text-neutral-500 text-sm">No domains added yet.</p>
      ) : (
        <ul className="divide-y divide-neutral-800 rounded-xl border border-neutral-800 bg-neutral-900 overflow-hidden">
          {domains.map((d) => (
            <li key={d.id} className="flex items-center justify-between px-4 py-3">
              <div>
                <p className="text-sm font-medium text-neutral-200">{d.domain}</p>
                <p className="text-xs text-neutral-600">
                  Added {new Date(d.createdAt).toLocaleDateString()}
                </p>
              </div>
              <Button
                variant="danger"
                size="sm"
                onClick={() => void handleRemove(d.id, d.domain)}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
